/**
 * completionChime.js
 *
 * RC UX.ENHANCEMENT — COMPLETION CHIME (LIGHTWEIGHT)
 * --------------------------------------------------
 * Long-running completion notification only.
 *
 * Plays a short, soft, two-note ascending fifth (A5 → E6) on a sine
 * oscillator when a long-running NDORFlow operation finishes
 * successfully — Analyse Fit, CV Variant, Cover Letter, Bulk
 * Evaluation, Governance Pack export.
 *
 * Doctrine:
 *   • Synthesised in-memory via the Web Audio API.
 *     No audio asset shipped; no network fetch.
 *   • Triggers ONLY on success terminal states.
 *     Never on errors, partial, failed, cancelled, button clicks,
 *     modal opens, navigation, validation, or polling ticks.
 *   • Silent failure on autoplay-blocked browsers.
 *     We do not request notification permissions; we do not surface
 *     errors to the user; if the browser refuses to play, the app
 *     continues uninterrupted.
 *   • 2-second dedupe window prevents polling-loop double-fire.
 *   • Future-proofed: a global mute toggle is read from
 *     `localStorage["ndorflow.chimeMuted"]`. A Settings UI can flip
 *     this at any time without code changes.
 *
 * Public API:
 *   • playCompletionChime()           — fire the chime (silent on fail)
 *   • setCompletionChimeMuted(bool)   — mute / unmute globally
 *   • isCompletionChimeMuted()        — current mute state
 *
 * No backend, no auth, no billing, no credits, no polling cadence
 * touched by this module.
 */

const LS_KEY = 'ndorflow.chimeMuted';
const DEDUPE_MS = 2000;

let lastPlayedAt = 0;

function isMuted() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(LS_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

export function isCompletionChimeMuted() {
  return isMuted();
}

export function setCompletionChimeMuted(muted) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (muted) {
      window.localStorage.setItem(LS_KEY, 'true');
    } else {
      window.localStorage.removeItem(LS_KEY);
    }
  } catch (_) {
    /* silent — chime mute is non-critical */
  }
}

/**
 * Long-running completion notification only.
 *
 * Plays the two-note chime once. Silently no-ops if:
 *   • window / AudioContext is unavailable (SSR or older browsers)
 *   • the user has muted via setCompletionChimeMuted(true)
 *   • the browser blocks autoplay (no user gesture yet)
 *   • another call fired within DEDUPE_MS milliseconds
 *
 * Safe to call from any client surface. Never throws.
 */
export function playCompletionChime() {
  try {
    if (typeof window === 'undefined') return;
    if (isMuted()) return;

    const now = Date.now();
    if (now - lastPlayedAt < DEDUPE_MS) return;
    lastPlayedAt = now;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;

    const ctx = new Ctx();
    const t0 = ctx.currentTime;

    // Two-note ascending fifth: A5 (880 Hz) → E6 (~1318.51 Hz)
    // Soft sine wave; total length ~310 ms; peak gain 0.07
    const notes = [
      { freq: 880.00, start: 0.000, dur: 0.160 },
      { freq: 1318.51, start: 0.130, dur: 0.180 },
    ];

    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.07;
    masterGain.connect(ctx.destination);

    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.value = 0;
      // Quick fade-in / fade-out for soft attack + release
      g.gain.setValueAtTime(0, t0 + start);
      g.gain.linearRampToValueAtTime(1, t0 + start + 0.020);
      g.gain.linearRampToValueAtTime(0, t0 + start + dur);
      osc.connect(g);
      g.connect(masterGain);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.010);
    });

    // Close the context after the chime completes — releases resources.
    setTimeout(() => {
      try { ctx.close(); } catch (_) { /* silent */ }
    }, 500);
  } catch (_) {
    /* silent — autoplay blocked or AudioContext unavailable */
  }
}
